import React, { useState, useContext } from 'react';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@mui/material/Button';
import { Link, useLocation } from 'react-router-dom';
import { Box, Menu, MenuItem, Divider, Avatar } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import GroupIcon from '@mui/icons-material/Group';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { StationContext } from '../context/StationContext';
import { UserContext } from '../context/UserContext';
import MyPermissionsDialog from './MyPermissionsDialog';
import NotificationBell from './NotificationBell';
import CreateNotificationDialog from './CreateNotificationDialog';
import logoFull from '../../assets/logo-full.png';
import {
  CALENDAR_PAGE_PERMISSIONS,
  CONFIGURATION_PERMISSIONS,
  getRoleLabel,
  hasAnyRequiredPermission,
  PERSONAL_PAGE_PERMISSIONS,
  REPORT_PERMISSIONS,
} from '../../config/routePermissions';

function Navbar({ shrink = false }) {
  const isMobile = useMediaQuery('(max-width:800px)');
  // Debajo de este ancho ya no entran los 4 links + Estación + Ayuda + usuario
  // con texto completo sin que se apreten entre sí -- se pasa a solo íconos
  // antes de llegar al breakpoint de mobile.
  const isCompact = useMediaQuery('(max-width:1440px)');
  const theme = useTheme();
  const location = useLocation();
  const { stations, selectedStation, selectStation, loading } = useContext(StationContext);
  const { user, permissions, logout } = useContext(UserContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [myPermissionsOpen, setMyPermissionsOpen] = useState(false);
  const [createNotificationOpen, setCreateNotificationOpen] = useState(false);
  const [notificationRefreshSignal, setNotificationRefreshSignal] = useState(0);
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';

  const enabledStations = stations.filter(st => st.enabled !== false);

  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const handleOpenMyPermissions = () => {
    handleUserMenuClose();
    setMyPermissionsOpen(true);
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleStationSelect = (station) => {
    selectStation(station);  // guarda en backend y actualiza UI
    handleMenuClose();
  };

  const handleLogout = async () => {
    await logout();
    handleMenuClose();
    window.location.href = "/login";
  };

  const iconSize = shrink ? 18 : 24;
  const canViewPersonal = hasAnyRequiredPermission(permissions, PERSONAL_PAGE_PERMISSIONS);
  const canViewCalendar = hasAnyRequiredPermission(permissions, CALENDAR_PAGE_PERMISSIONS);
  const canViewConfiguration = hasAnyRequiredPermission(permissions, CONFIGURATION_PERMISSIONS);
  const canViewReports = hasAnyRequiredPermission(permissions, REPORT_PERMISSIONS);

  const getButtonStyle = (paths) => {
    const isActive =
      (location.pathname === '/' && paths.includes('/personal_table')) ||
      paths.some((path) => location.pathname.startsWith(path));
  
    return {
      color: isActive ? theme.palette.primary.main : theme.palette.secondary.main,
      backgroundColor: isActive ? theme.palette.secondary.light : 'transparent',
      ml: 2,
      height: '100%',
      minHeight: 0,
      borderRadius: 0,
      fontSize: shrink ? '0.8rem' : '0.9rem',
      display: 'flex',
      borderBottom: '3px solid transparent',
      transition: 'all 0.3s',
      paddingTop: 0,
      paddingBottom: shrink ? '2px' : '6px',
      px: shrink ? 1 : 2,
      py: 0,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.secondary.light,
        borderBottom: `3px solid ${theme.palette.secondary.light}`,
        borderRadius: 0,
      },
    };
  };

  function renderMenuButton({ to, icon, text, paths, tooltip }) {
    const showText = !isMobile && !isCompact;
    const button = (
      <Button component={Link} to={to} sx={getButtonStyle(paths)} startIcon={icon}>
        {showText && text}
      </Button>
    );
    return showText ? button : <Tooltip title={tooltip} arrow>{button}</Tooltip>;
  }

  return (
    <>
      <Box
        component={Link}
        to="/"
        sx={{
          mr: 4,
          transition: 'padding 0.3s, background-color 0.2s',
          paddingLeft: { xs: 1, sm: 2, md: 3 },
          paddingRight: { xs: 1, sm: 1.5 },
          alignSelf: 'center',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          minWidth: 0,
          borderRadius: 1,
          textDecoration: 'none',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <Box
          component="img"
          src={logoFull}
          alt="Time Planner"
          sx={{
            height: isMobile ? 40 : (shrink ? 34 : 60),
            width: 'auto',
            transition: 'height 0.3s',
            display: 'block',
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
        {canViewPersonal && renderMenuButton({
          to: "/personal_table",
          icon: <GroupIcon sx={{ fontSize: iconSize }} />,
          text: "Personal",
          paths: ['/personal_table', '/personal_edit'],
          tooltip: "Personal",
        })}

        {canViewCalendar && renderMenuButton({
          to: "/planificador",
          icon: <CalendarTodayIcon sx={{ fontSize: iconSize }} />,
          text: "Planificador",
          paths: ['/planificador'],
          tooltip: "Planificador",
        })}

        {canViewConfiguration && renderMenuButton({
          to: "/configuracion",
          icon: <SettingsIcon sx={{ fontSize: iconSize }} />,
          text: "Configuración",
          paths: ['/configuracion'],
          tooltip: "Configuración",
        })}

        {canViewReports && renderMenuButton({
          to: "/reportes",
          icon: <DescriptionIcon sx={{ fontSize: iconSize }} />,
          text: "Reportes",
          paths: ['/reportes'],
          tooltip: "Reportes",
        })}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          sx={{
            color: theme.palette.secondary.main,
            textTransform: 'none',
            display: 'flex',
            alignItems: 'center',
            minHeight: 0,
            fontSize: shrink ? '0.8rem' : '0.9rem',
            px: shrink ? 1 : 2,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.secondary.light,
              borderBottom: `3px solid ${theme.palette.secondary.light}`,
              borderRadius: 0,
            },
            transition: 'all 0.3s',
            py: 0,
            height: '100%',
            paddingTop: 0,
            borderRadius: 0,
          }}
          onClick={handleMenuOpen}
          disabled={ enabledStations.length === 0}
          endIcon={<ArrowDropDownIcon sx={{ ml: 1, fontSize: iconSize }} />}
        >
          {isCompact ? '' : 'Estación: '}{loading ? 'Cargando…' : selectedStation.name}
        </Button>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {enabledStations.length === 0 && <MenuItem disabled>(Sin estaciones habilitadas)</MenuItem>}
        {enabledStations.map(st => (
          <MenuItem key={st.id} disabled={loading} onClick={() => handleStationSelect(st)}>
            {st.name}
          </MenuItem>
        ))}
      </Menu>

        {renderMenuButton({
          to: "/ayuda",
          icon: <HelpOutlineIcon sx={{ fontSize: iconSize }} />,
          text: "Ayuda",
          paths: ['/ayuda'],
          tooltip: "Ayuda",
        })}

        <NotificationBell refreshSignal={notificationRefreshSignal} />

        {isSuperAdmin && (
          <Tooltip title="Enviar notificación" arrow>
            <Box
              component="button"
              onClick={() => setCreateNotificationOpen(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: 'transparent',
                color: theme.palette.secondary.main,
                cursor: 'pointer',
                borderRadius: '50%',
                width: 40,
                height: 40,
                '&:hover': { backgroundColor: theme.palette.action.hover },
              }}
            >
              <AddAlertIcon sx={{ fontSize: iconSize }} />
            </Box>
          </Tooltip>
        )}

        <CreateNotificationDialog
          open={createNotificationOpen}
          onClose={() => setCreateNotificationOpen(false)}
          onCreated={() => setNotificationRefreshSignal((k) => k + 1)}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 3 }}>
          <Button
            sx={{
              textTransform: 'none',
              p: 0,
              minWidth: 0,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: theme.palette.action.hover }
            }}
            onClick={handleUserMenuOpen}
          >
            <Avatar sx={{ bgcolor: "#00E875", width: 32, height: 32, fontSize: 16 }}>
              {user.name ? user.name[0].toUpperCase() : "U"}
            </Avatar>
            {!isMobile && !isCompact && (
              <Typography sx={{ ml: 1, fontWeight: 500, color: theme.palette.secondary.main, fontSize: shrink ? '0.9rem' : '1rem' }}>
                {user.name || "Usuario"}
              </Typography>
            )}
          </Button>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top',    horizontal: 'right' }}
            PopperProps={{
              modifiers: [{ name: 'offset', options: { offset: [0, 10] } }],
            }}
            PaperProps={{
              elevation: 4,
              sx: {
                mt: 1,          // redundante con offset, pero deja un margen suave
                minWidth: 260,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              },
            }}
            keepMounted
            disableScrollLock
          >
            <MenuItem disabled sx={{ display: 'block', pt: 1, pb: 1.5, minWidth: '220px' }}>
              <Typography sx={{ fontWeight: 700, color: theme.palette.grey[800], mb: 0.2 }}>
                {user.name || "Usuario"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: theme.palette.grey[500], overflowWrap: 'break-word' }}>
                {user.email || ""}
              </Typography>
              <Typography
                sx={{
                  display: 'inline-block',
                  mt: 1,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.palette.primary.dark,
                  backgroundColor: theme.palette.secondary.light,
                }}
              >
                {getRoleLabel(user.role)}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleOpenMyPermissions}>
              Mis permisos
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ fontWeight: 500, color: theme.palette.primary.dark }}>
              Cerrar sesión
            </MenuItem>
          </Menu>

          <MyPermissionsDialog
            open={myPermissionsOpen}
            onClose={() => setMyPermissionsOpen(false)}
            role={user.role}
            permissions={permissions}
          />
        </Box>
      </Box>
    </>
  );
}

export default Navbar;
